import { useMemo } from "react"
import { useApi } from "../hooks/useApi"
import type { QueryStateApi } from "../hooks/useQueryState"
import { api } from "../services/api"
import { ErrorState, SkeletonRows } from "../components/ui"
import { formatHours, monthLabel, NO_DEPARTMENT, recentMonths } from "../lib/format"
import "../styles/table.scss"

/**
 * La vista AGREGADA del tablero: horas extra APROBADAS por persona en un mes.
 *
 * Lo que NO muestra, a propósito: solicitudes pendientes o rechazadas. Esto es lo
 * que se paga, no lo que se pidió; el circuito de decisión vive en Slack, y el
 * detalle solicitud por solicitud —con TODOS los estados y su justificación— es
 * la pestaña "Solicitudes" (RequestsPage). Un mes con solicitudes sin resolver se
 * ve vacío acá, y eso es correcto — el filtro está en la vista SQL
 * (supabase/dashboard.sql), no acá.
 *
 * `query`/`setQuery` llegan por props desde App: useQueryState se instancia UNA
 * sola vez (QueryStateApi explica por qué).
 */
export function OvertimePage({ query, setQuery }: QueryStateApi) {
  const months = useMemo(() => recentMonths(), [])
  // Un ?month= inválido o viejo (fuera de los 24) se ignora y cae en el mes actual,
  // que es months[0]. Así un link guardado nunca deja la pantalla en un estado raro.
  const month = query.month && months.includes(query.month) ? query.month : months[0]
  // El departamento NO se puede validar contra una lista local: las opciones las
  // trae la respuesta, y dependen del mes. Se manda tal cual y un valor que no
  // existe simplemente da cero filas — el <select> cae a "Todos" porque su value
  // no matchea ninguna <option> (ver el fallback de abajo).
  const department = query.department ?? null

  const { data, error, loading, isRefetching, refetch } = useApi(
    (signal) => api.overtime(month, department, signal),
    [month, department]
  )

  // Un departamento elegido que ya no está entre las opciones del mes se sigue
  // ofreciendo mientras esté aplicado: sin esto el <select> mostraría "Todos"
  // mientras la tabla muestra un mes filtrado, y no habría cómo salir del filtro.
  const departmentOptions = data
    ? department && !data.departments.includes(department)
      ? [department, ...data.departments]
      : data.departments
    : department
      ? [department]
      : []

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Horas extra por persona</h1>
          <p className="page-subtitle">
            Horas aprobadas en {monthLabel(month)}
            {department ? ` · ${department}` : ""}. Incluye las aprobaciones parciales,
            por las horas efectivamente aprobadas.
          </p>
        </div>
        <div className="page-actions">
          <div className="month-picker">
            <label htmlFor="month">Mes</label>
            <select
              id="month"
              value={month}
              onChange={(e) => setQuery({ month: e.target.value })}
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </div>
          {/* El selector solo aparece si el mes trae departamentos: en un workspace
              sin user groups, o en meses anteriores a la feature, un <select> con
              una sola opción sería ruido. */}
          {departmentOptions.length > 0 && (
            <div className="month-picker">
              <label htmlFor="department">Departamento</label>
              <select
                id="department"
                value={department ?? ""}
                // undefined borra la clave del query string (ver useQueryState), así
                // que "Todos" deja la URL igual que si nunca se hubiera filtrado.
                onChange={(e) => setQuery({ department: e.target.value || undefined })}
              >
                <option value="">Todos</option>
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button type="button" className="btn btn-sm" onClick={refetch} disabled={isRefetching}>
            {isRefetching ? "Actualizando…" : "Actualizar"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="card">
          <ErrorState
            title="No se pudieron cargar las horas"
            message={error.message}
            onRetry={refetch}
          />
        </div>
      ) : (
        <>
          {/* is-refetching atenúa la tabla anterior en vez de mostrar skeleton: cambiar
              de mes no debe producir un flash ni un salto de layout (ver useApi). */}
          <div className={`table-wrap${isRefetching ? " is-refetching" : ""}`}>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Empleado</th>
                  <th scope="col">Departamento</th>
                  <th scope="col" className="numeric">
                    Solicitudes
                  </th>
                  <th scope="col" className="numeric">
                    Horas aprobadas
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows rows={5} cols={4} />
                ) : data && data.rows.length > 0 ? (
                  data.rows.map((r) => (
                    <tr key={r.employee_slack_id}>
                      <td>{r.employee_name}</td>
                      {/* El departamento es opcional en el formulario y no existía
                          en las filas viejas: el guión dice "no cargado", no "sin equipo". */}
                      <td>{r.department_name ?? NO_DEPARTMENT}</td>
                      <td className="numeric">{r.requests}</td>
                      <td className="numeric">{formatHours(r.hours_approved)}</td>
                    </tr>
                  ))
                ) : (
                  // El vacío va DENTRO de la tabla y no en un bloque aparte: con el
                  // encabezado a la vista queda claro que la consulta funcionó y que
                  // el mes no tiene nada, en vez de parecer una tabla que no cargó.
                  <tr>
                    <td className="empty" colSpan={4}>
                      <div className="state-title">
                        Nada que pagar en {monthLabel(month)}
                        {department ? ` en ${department}` : ""}
                      </div>
                      {department
                        ? "Puede que el mes tenga horas en otros departamentos: probá con «Todos»."
                        : "Solo se cuentan las solicitudes ya aprobadas. Si hay horas cargadas esperando decisión, todavía no aparecen acá."}
                    </td>
                  </tr>
                )}
              </tbody>
              {/* El total va en un tfoot y no en un card aparte: así se imprime y se
                  copia junto con las filas, y queda alineado con la columna que suma.
                  Los dos números los calcula el SERVIDOR, sobre las mismas filas que
                  devuelve, así que la tabla y el total no pueden discrepar. */}
              {data && data.rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    {/* Vacía: el total de una columna de nombres no significa nada. */}
                    <td></td>
                    <td className="numeric">{data.total_requests}</td>
                    <td className="numeric">{formatHours(data.total_hours)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {data && (
            <p className="freshness">
              Datos al{" "}
              {new Date(data.generated_at).toLocaleTimeString("es", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              .
            </p>
          )}
        </>
      )}
    </>
  )
}
